import { Form, InputNumber, Input, Message, Modal, Radio } from '@arco-design/web-react';
import { useEffect } from 'react';
import { walletApi } from '@/api';
import { dollarsToCents } from '@/utils/money';
import type { CustomerSummary } from '@/types/customer';

interface Props {
  customer: CustomerSummary | null;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormState {
  direction: 'CREDIT' | 'DEBIT';
  amount: number;
  reason: string;
}

export default function WalletTransferModal({ customer, visible, onClose, onSuccess }: Props) {
  const [form] = Form.useForm<FormState>();

  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue({ direction: 'CREDIT' });
    }
  }, [visible, form]);

  const handleOk = async () => {
    if (!customer) return;
    const values = await form.validate();
    await walletApi.transfer({
      customerId: customer.id,
      direction: values.direction,
      amountCents: dollarsToCents(values.amount),
      reason: values.reason.trim(),
    });
    Message.success('划拨成功');
    onSuccess();
    onClose();
  };

  return (
    <Modal
      title={`为「${customer?.username ?? ''}」划拨资金`}
      visible={visible}
      onOk={handleOk}
      onCancel={onClose}
      okText="确认提交"
      cancelText="取消"
      maskClosable={false}
    >
      <Form form={form} layout="vertical">
        <Form.Item label="方向" field="direction" rules={[{ required: true }]}>
          <Radio.Group>
            <Radio value="CREDIT">转入（母账户 +）</Radio>
            <Radio value="DEBIT">转出（母账户 -）</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label="金额（USD）"
          field="amount"
          rules={[
            { required: true, message: '请输入金额' },
            {
              validator: (val, cb) => {
                if (!val || val <= 0) cb('金额必须大于 0');
                else cb();
              },
            },
          ]}
        >
          <InputNumber min={0.01} precision={2} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="操作原因（将写入审计日志）"
          field="reason"
          rules={[
            { required: true, message: '请填写原因' },
            { minLength: 4, message: '原因至少 4 个字符' },
          ]}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={200} showWordLimit />
        </Form.Item>
      </Form>
    </Modal>
  );
}
